import * as React from 'react'
import { useState, useCallback, useEffect, useRef } from 'react'
import { GripVertical, PenLine, Eye } from 'lucide-react'
import { cn } from '../utils'

export type MobileView = 'editor' | 'preview'

export interface ResizablePanelsProps {
  left: React.ReactNode
  right: React.ReactNode
  defaultLeftWidth?: number
  minLeftWidth?: number
  maxLeftWidth?: number
  keyboardStep?: number
  onWidthChange?: (leftWidth: number) => void
  mobileBreakpoint?: number
  className?: string
}

export function ResizablePanels({
  left,
  right,
  defaultLeftWidth = 420,
  minLeftWidth = 280,
  maxLeftWidth = 800,
  keyboardStep = 50,
  onWidthChange,
  mobileBreakpoint = 768,
  className,
}: ResizablePanelsProps) {
  const [leftWidth, setLeftWidth] = useState(defaultLeftWidth)
  const [isDragging, setIsDragging] = useState(false)
  const [isMobile, setIsMobile] = useState(false)
  const [mobileView, setMobileView] = useState<MobileView>('editor')
  const containerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < mobileBreakpoint)
    checkMobile()
    window.addEventListener('resize', checkMobile)
    return () => window.removeEventListener('resize', checkMobile)
  }, [mobileBreakpoint])

  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault()
    setIsDragging(true)
  }, [])

  useEffect(() => {
    if (!isDragging) return

    const handleMouseMove = (e: MouseEvent) => {
      if (!containerRef.current) return
      const containerRect = containerRef.current.getBoundingClientRect()
      const newWidth = e.clientX - containerRect.left
      const clampedWidth = Math.min(Math.max(newWidth, minLeftWidth), maxLeftWidth)
      setLeftWidth(clampedWidth)
      onWidthChange?.(clampedWidth)
    }

    const handleMouseUp = () => setIsDragging(false)

    document.addEventListener('mousemove', handleMouseMove)
    document.addEventListener('mouseup', handleMouseUp)
    document.body.style.userSelect = 'none'
    document.body.style.cursor = 'col-resize'

    return () => {
      document.removeEventListener('mousemove', handleMouseMove)
      document.removeEventListener('mouseup', handleMouseUp)
      document.body.style.userSelect = ''
      document.body.style.cursor = ''
    }
  }, [isDragging, minLeftWidth, maxLeftWidth, onWidthChange])

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!e.altKey) return
      const isInComponent = containerRef.current?.contains(document.activeElement) || document.activeElement === document.body
      if (!isInComponent) return

      if (e.key === 'ArrowLeft') {
        e.preventDefault()
        const newWidth = Math.max(leftWidth - keyboardStep, minLeftWidth)
        setLeftWidth(newWidth)
        onWidthChange?.(newWidth)
      } else if (e.key === 'ArrowRight') {
        e.preventDefault()
        const newWidth = Math.min(leftWidth + keyboardStep, maxLeftWidth)
        setLeftWidth(newWidth)
        onWidthChange?.(newWidth)
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [leftWidth, keyboardStep, minLeftWidth, maxLeftWidth, onWidthChange])

  const handleDoubleClick = useCallback(() => {
    setLeftWidth(defaultLeftWidth)
    onWidthChange?.(defaultLeftWidth)
  }, [defaultLeftWidth, onWidthChange])

  if (isMobile) {
    return (
      <div className={cn('flex flex-col h-full w-full', className)}>
        <div className="flex border-b border-gray-200 bg-white">
          <button
            type="button"
            onClick={() => setMobileView('editor')}
            className={cn(
              'flex-1 flex items-center justify-center gap-2 px-4 py-3',
              'text-sm font-medium border-b-2 transition-colors',
              'focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-inset',
              mobileView === 'editor'
                ? 'border-primary-500 text-primary-600 bg-primary-50'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            )}
          >
            <PenLine className="w-4 h-4" />
            Editor
          </button>
          <button
            type="button"
            onClick={() => setMobileView('preview')}
            className={cn(
              'flex-1 flex items-center justify-center gap-2 px-4 py-3',
              'text-sm font-medium border-b-2 transition-colors',
              'focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-inset',
              mobileView === 'preview'
                ? 'border-primary-500 text-primary-600 bg-primary-50'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            )}
          >
            <Eye className="w-4 h-4" />
            Preview
          </button>
        </div>
        <div className="flex-1 overflow-hidden">
          {mobileView === 'editor' ? left : right}
        </div>
      </div>
    )
  }

  return (
    <div ref={containerRef} className={cn('flex h-full w-full overflow-hidden', className)}>
      <div className="flex-shrink-0 overflow-hidden" style={{ width: leftWidth }}>
        {left}
      </div>

      <div
        role="separator"
        aria-orientation="vertical"
        aria-valuenow={leftWidth}
        aria-valuemin={minLeftWidth}
        aria-valuemax={maxLeftWidth}
        aria-label="Resize panels. Use Alt+Left or Alt+Right to resize with keyboard."
        tabIndex={0}
        onMouseDown={handleMouseDown}
        onDoubleClick={handleDoubleClick}
        className={cn(
          'relative flex-shrink-0 w-1 cursor-col-resize',
          'bg-gray-200 hover:bg-primary-400',
          'group flex items-center justify-center',
          'focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500',
          isDragging && 'bg-primary-500',
          'transition-all duration-150 ease-out'
        )}
      >
        <div
          className={cn(
            'absolute inset-y-0 -left-1 -right-1',
            'flex items-center justify-center',
            'opacity-0 group-hover:opacity-100',
            isDragging && 'opacity-100',
            'transition-opacity duration-150 ease-out'
          )}
        >
          <div className="flex items-center justify-center w-4 h-8 bg-gray-300 rounded-sm shadow-sm">
            <GripVertical className="w-3 h-3 text-gray-600" />
          </div>
        </div>
      </div>

      <div className="flex-1 overflow-hidden min-w-0">
        {right}
      </div>
    </div>
  )
}
